import {
  Typography,
  Grid,
  Box,
  Link,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import { Fragment, useCallback, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { time } from "./time";
import { ICON_BASE_PATH, IMAGE_BASE_PATH } from "../../config/paths";
import ButtonsFilter from "../../components/buttons/ButtonsFilter";
import { useSeo } from "../../components/seo/useSeo";
import { useJsonLd } from "../../components/seo/useJsonLd";
import { useLinkLang } from "../../hooks/useLinkLang";

interface TimeBaseProps {
  pageSlug: string; // "atletas" | "embaixadores" (para canonical e ids)
  timeNomeKey: string;
  timeDescricaoKey: string;
  tipo: "ATLETA" | "EMBAIXADOR";
  excludedCountries?: string[];
  // opcionais, se quiser enriquecer OG
  ogImage?: string; // ideal: URL absoluta, mas pode deixar vazio
}

function getCanonical(path: string) {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  return `${origin}${path}`;
}

const TimeBase = (props: TimeBaseProps) => {
  const {
    pageSlug,
    timeNomeKey,
    timeDescricaoKey,
    tipo,
    excludedCountries,
    ogImage,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation();

  const linkLang = useLinkLang();

  // Traduções (uma vez)
  const titleText = t(timeNomeKey);
  const descText = t(timeDescricaoKey);

  // Canonical
  const canonical = getCanonical(`/${pageSlug}`);

  // SEO head (SPA)
  useSeo({
    title: `${titleText} | CORE`,
    description: descText,
    canonical,
    image: ogImage,
    type: "website",
  });

  // Label LATAM deve acompanhar idioma
  const LATAM_LABEL = "LATAM";
  const [filtro, setFiltro] = useState<string>(LATAM_LABEL);

  useEffect(() => {
    // quando muda idioma, atualiza o estado de filtro para o novo label
    setFiltro(LATAM_LABEL);
  }, [LATAM_LABEL]);

  const timeFiltrado = useMemo(() => {
    const timeTipo = time.filter((pessoa) =>
      tipo === "ATLETA" ? pessoa.atleta : pessoa.embaixador
    );

    return timeTipo.filter((pessoa) => {
      if (filtro === LATAM_LABEL) return true;
      return pessoa.pais.substring(0, 2) === filtro.substring(0, 2);
    });
  }, [tipo, filtro, LATAM_LABEL]);

  const manageFlagIcons = useCallback(
    (pais: string) => {
      const common = { style: { width: "36px", height: "36px" } };
      switch (pais) {
        case "URUGUAI":
          return (
            <img
              src={`${ICON_BASE_PATH}UruguaiIcon.svg`}
              alt={t("Bandeiras.Uruguai")}
              {...common}
              loading="lazy"
              decoding="async"
            />
          );
        case "ARGENTINA":
          return (
            <img
              src={`${ICON_BASE_PATH}ArgentinaIcon.svg`}
              alt={t("Bandeiras.Argentina")}
              {...common}
              loading="lazy"
              decoding="async"
            />
          );
        case "PERU":
          return (
            <img
              src={`${ICON_BASE_PATH}PeruIcon.svg`}
              alt={t("Bandeiras.Peru")}
              {...common}
              loading="lazy"
              decoding="async"
            />
          );
        case "CHILE":
          return (
            <img
              src={`${ICON_BASE_PATH}ChileIcon.svg`}
              alt={t("Bandeiras.Chile")}
              {...common}
              loading="lazy"
              decoding="async"
            />
          );
        default:
          return (
            <img
              src={`${ICON_BASE_PATH}BrazilIcon.svg`}
              alt={t("Bandeiras.Brazil")}
              {...common}
              loading="lazy"
              decoding="async"
            />
          );
      }
    },
    [t]
  );

  const manageFlagColors = useCallback((pais: string) => {
    switch (pais) {
      case "ALEMANHA":
        return "linear-gradient(90deg, #000000 0%, #DD0000 50%, #FFCC00 60%)";
      case "ARGENTINA":
        return "linear-gradient(90deg, #74ACDF 0%, #FFFFFF 50%, #74ACDF 100%)";
      case "URUGUAI":
        return "linear-gradient(90deg, #FFFFFF 0%, #3A75C4 50%, #FFFFFF 100%)";
      case "PERU":
        return "linear-gradient(90deg, #D91023 0%, #FFFFFF 50%, #D91023 100%)";
      case "CHILE":
        return "linear-gradient(90deg, #0039A6 0%, #FFFFFF 50%, #D52B1E 100%)";
      default:
        return "linear-gradient(90deg, #FFDE00 0%, #009B3A 70%, #0033A0 100%)";
    }
  }, []);

  const manageFavoriteKite = useCallback((kite: string) => {
    switch (kite) {
      case "PACE PRO":
        return "https://ridecore.com/" + linkLang + "/kite/kites/pace-pro";
      case "XR PRO":
        return "https://ridecore.com/" + linkLang + "/kite/kites/xr-pro";
      case "XR8":
        return "https://ridecore.com/" + linkLang + "/kite/kites/xr8";
      case "NEXUS 4":
        return "https://ridecore.com/" + linkLang + "/kite/kites/nexus-4";
      case "SECTION 5":
        return "https://ridecore.com/" + linkLang + "/kite/kites/section-5";
      case "GTS 6":
        return "https://ridecore.com/" + linkLang + "/kite/kites/gts6";
      case "XLITE 2":
        return "https://ridecore.com/" + linkLang + "/kite/kites/xlite-2";
      case "IMPACT 2":
        return "https://ridecore.com/" + linkLang + "/kite/kites/impact-2";
      case "XC":
        return "https://ridecore.com/" + linkLang + "/wing/wings/xc";
      case "HALO":
        return "https://ridecore.com/" + linkLang + "/wing/wings/halo";
      case "HALO PRO":
        return "https://ridecore.com/" + linkLang + "/wing/wings/halo-pro";
      default:
        return "https://ridecore.com/" + linkLang + "/kite/kite-range";
    }
  }, []);

  // JSON-LD (CollectionPage + ItemList de pessoas)
  const jsonLd = useMemo(() => {
    const people = timeFiltrado.map((p) => ({
      "@type": "Person",
      name: p.nome,
      image: p.imagem,
      homeLocation: p.local ? { "@type": "Place", name: p.local } : undefined,
      sameAs: p.instagram
        ? [`https://www.instagram.com/${p.instagram.replace("@", "")}/`]
        : undefined,
    }));

    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: titleText,
      description: descText,
      inLanguage: i18n.language,
      url: canonical,
      mainEntity: {
        "@type": "ItemList",
        itemListElement: people.map((person, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          item: person,
        })),
      },
    };
  }, [timeFiltrado, titleText, descText, i18n.language, canonical]);

  useJsonLd(pageSlug, jsonLd);

  const titleId = `${pageSlug}-title`;
  const descId = `${pageSlug}-desc`;

  const heroSrc =
    tipo === "ATLETA"
      ? isMobile
        ? `${IMAGE_BASE_PATH}atletas/AtletasBanner.avif`
        : `${IMAGE_BASE_PATH}atletas/AtletasBanner2.avif`
      : isMobile
        ? `${IMAGE_BASE_PATH}embaixadores/EmbaixadoresBannerMobile.avif`
        : `${IMAGE_BASE_PATH}embaixadores/EmbaixadoresBanner2.avif`;

  return (
    <Box component="main" aria-labelledby={titleId} aria-describedby={descId}>
      <Grid container spacing={4} justifyContent="center" sx={{ bgcolor: "white" }}>
        {/* Header / Hero */}
        <Grid size={12} height={{ xs: "80vh", sm: "60vh" }}>
          <Box component="header" sx={{ position: "relative", height: "100%", overflow: "hidden" }}>
            <img
              src={heroSrc}
              alt={tipo === "ATLETA" ? t("TimeBase.BannerAtletas") : t("TimeBase.BannerEmbaixadores")}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              fetchPriority="high"
              decoding="async"
            />
          </Box>
        </Grid>

        {/* H1 */}
        <Grid size={12}>
          <Typography
            id={titleId}
            component="h1"
            variant="h1"
            fontSize={{ xs: 45, sm: 65, md: 85, lg: 100, xl: 100 }}
            sx={{
              color: "black",
              textAlign: "center",
              fontFamily: "PFDinTextPro-Thin, sans-serif",
              fontWeight: 100,
            }}
          >
            {titleText}
          </Typography>
        </Grid>

        {/* Descrição */}
        <Grid
          size={12}
          paddingLeft={{ xs: 5, sm: 10, md: 15, lg: 25, xl: 25 }}
          paddingRight={{ xs: 5, sm: 10, md: 15, lg: 25, xl: 25 }}
          marginBottom={3}
        >
          <Typography
            id={descId}
            component="p"
            variant="body1"
            fontSize={{ xs: 14, sm: 21, md: 21, lg: 21, xl: 21 }}
            sx={{
              color: "black",
              textAlign: "center",
              fontFamily: "Helvetica,Arial,Verdana,sans-serif",
              fontWeight: 100,
            }}
          >
            {descText}
          </Typography>
        </Grid>

        {/* Filtros */}
        <Grid size={12} textAlign="center" px={2}>
          <Box component="section" aria-label={t("TimeBase.FiltrosPais")}>
            <ButtonsFilter
              selected={filtro}
              setSelected={setFiltro}
              excludedCountries={excludedCountries}
            />
          </Box>
        </Grid>

        {/* Lista */}
        <Grid
          component="section"
          aria-label={t("TimeBase.ListaTime")}
          container
          spacing={4}
          justifyContent="center"
          sx={{
            pb: { xs: 2, sm: 5, md: 10, lg: 10, xl: 30 },
            pl: { xs: 2, sm: 5, md: 10, lg: 10, xl: 30 },
            pr: { xs: 2, sm: 5, md: 10, lg: 10, xl: 30 },
          }}
        >
          {timeFiltrado.map((member) => {
            const instagramUrl = member.instagram
              ? `https://www.instagram.com/${member.instagram.replace("@", "")}/`
              : null;

            return (
              <Grid
                key={member.nome}
                size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 3 }}
                marginBottom={3}
                minWidth={300}
                maxWidth={350}
                component="article"
                aria-label={`${member.nome} — ${member.local}`}
              >
                <Box component="figure" sx={{ width: "100%", maxWidth: "100%", m: 0, display: "inline-block" }}>
                  <Box
                    component="img"
                    sx={{
                      height: 300,
                      width: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                    alt={`${member.nome} — ${member.local}`}
                    src={member.imagem}
                    loading="lazy"
                    decoding="async"
                  />
                  <Box aria-hidden="true" sx={{ background: manageFlagColors(member.pais), height: "4px" }} />
                  <Box component="figcaption" sx={{ display: "none" }}>
                    {member.nome} — {member.local}
                  </Box>
                </Box>

                {/* Nome */}
                <Box display="flex" justifyContent="center" mb={2}>
                  <Box display="flex" flexDirection="column" alignItems="flex-end">
                    <Box display="flex" alignItems="center">
                      {manageFlagIcons(member.pais)}
                      <Typography
                        component="h2"
                        variant="h6"
                        gutterBottom
                        sx={{
                          fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                          fontWeight: 400,
                          color: "black",
                          ml: 1,
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          mb: 0,
                        }}
                      >
                        {member.nome}
                      </Typography>
                    </Box>
                    <Typography
                      component="p"
                      variant="body2"
                      sx={{
                        fontFamily: '"Barlow Condensed", Arial, sans-serif',
                        fontWeight: 500,
                        color: "black",
                        mt: -1.4,
                      }}
                    >
                      {member.local}
                    </Typography>
                  </Box>
                </Box>

                {/* Kites (ul/li) */}
                <Box textAlign="center" mb={1}>
                  <Box
                    component="ul"
                    aria-label={t("TimeBase.EquipamentoUtilizadoAria")}
                    sx={{
                      listStyle: "none",
                      p: 0,
                      m: 0,
                      display: "flex",
                      justifyContent: "space-around",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    {member.kiteFavorito.map((kite, idx) => (
                      <Fragment key={`${member.nome}-${idx}`}>
                        <Tooltip title={t("Timebase.EquipamentoUtilizado")}>
                          <Box component="li" sx={{ display: "flex", alignItems: "center" }}>
                            <Box
                              component="img"
                              sx={{ width: 18, height: 18 }}
                              alt={t("TimeBase.IconeKite")}
                              src={`${ICON_BASE_PATH}KiteIcon.png`}
                              loading="lazy"
                              decoding="async"
                            />
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                fontSize: { xs: 12, sm: 11, md: 11.5, lg: 13, xl: 13 },
                                fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                                fontWeight: 700,
                                ml: 0.5,
                                mr: 1,
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <Link
                                href={manageFavoriteKite(kite)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: "black", textDecorationColor: "#FFDE00", ml: "5px" }}
                                aria-label={`${t("TimeBase.EquipamentosPaginaAria")} ${kite}`}
                              >
                                {kite}
                              </Link>
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Fragment>
                    ))}
                  </Box>
                </Box>

                {/* Instagram */}
                <Box
                  display="flex"
                  justifyContent="center"
                  role="contentinfo"
                  aria-label={"Instagram"}
                  sx={{
                    bgcolor: "#FFDE00",
                    mt: 2.5,
                    py: 0.5,
                    px: 2,
                    mx: "auto",
                    borderRadius: 1,
                  }}
                >
                  <InstagramIcon sx={{ color: "black", fontSize: 25, mt: 0.5 }} />
                  {instagramUrl ? (
                    <Link
                      href={instagramUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{ textDecoration: "none", ml: 1 }}
                      aria-label={`${t("TimeBase.AbrirInstagramAria")} ${member.nome}`}
                    >
                      <Typography
                        component="span"
                        variant="body1"
                        sx={{
                          fontFamily: "Helvetica,Arial,Verdana,sans-serif",
                          fontWeight: 700,
                          color: "black",
                          ml: "8px",
                        }}
                      >
                        {member.instagram}
                      </Typography>
                    </Link>
                  ) : (
                    <Box aria-hidden="true" />
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Box>
  );
};

export default TimeBase;
